import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  addAttributesToContent,
  createFolder,
  DesignerRequestVerb,
  dropFile,
  EDITOR,
  FileFolderType,
  FileFolderTypeWithContent,
  FolderRootPath,
  FolderRootType,
  getFileExtentionFromType,
  getFilesInfo,
  getNodeInfo,
  getTypeFromExtension,
  IFileInfo,
  INodeInfo,
  ISaveFileResponse,
  ISetFileContentResponse,
  ITabFlags,
  ITextEditorSelection,
  moveItem,
  parseAttributes,
  removeItem,
  ROLES_TAB_PATH,
  HTTP_HANDLERS_PATH,
  setFileContent,
  SETTINGS_TAB_PATH,
  ToastKind,
  toastSubject,
  LOCALIZATION_PATH,
} from '@qodly/shared';
import { CATALOG_REFRESH_DELAY } from '~store/utils';
import {
  ERROR_CODES,
  GetNodeInfoException,
  ITab,
  ITabExtra,
  ModalColor,
  ModalType,
} from '@qodly/shared';
import { ClassFileTypes, EDisplayMode } from '~store/modules/root/types';
import { isJSON } from '~store/utils';
import { AppState, closeTab, fetchCatalog, openModal } from '..';
import { setRolesContent } from '../roles';
import { openTabUnselected, selectTabByReference } from '../root';
import { ISettingsState, setSettings } from '../settings';
import { addNewFile, findNodeByPath, removeFile } from './reducer';
import { IOpenFilePayload, ITreeItem } from './types';
import { createClassesFolderContent, getContent, getEditors } from './utils';
import { dbMethods } from './dbMethods';
import { values } from 'lodash';

interface ICreateNewFilePayload {
  fileName: string;
  type: FileFolderType;
  path: string;
  parentNode: ITreeItem;
  content?: string;
  qodly: boolean;
}

export function getDefaultContent(
  root: FileFolderType,
  projectSettings: ISettingsState['project'],
) {
  switch (root) {
    case FileFolderType.WEBFORMS: {
      const webformContent = {
        metadata: {
          v: '1.0',
          datasources: [],
          styles: [],
        },
        components: {
          ROOT: {
            parent: '',
            type: {
              resolvedName: 'Container',
            },
            isCanvas: true,
            props: {
              airyMode: false,
              className: 'bg-white h-full w-full px-6',
              classNames: [],
              events: [],
            },
            displayName: 'Page',
            custom: {},
            hidden: false,
            nodes: [],
            linkedNodes: {},
          },
        },
      };
      if (projectSettings['webforms.display-mode'] === EDisplayMode.AIRY) {
        webformContent.components.ROOT.props.airyMode = true;
      }
      return webformContent;
    }
    case FileFolderType.REPORT: {
      return {
        metadata: {
          v: '1.0',
          datasources: [],
          styles: [],
        },
        pages: [
          {
            name: 'doc',
            id: 'index',
            components: {
              ROOT: {
                type: {
                  resolvedName: 'Document',
                },
                isCanvas: true,
                props: {
                  classNames: [],
                  events: [],
                  airyMode: false,
                  className: 'bg-white h-full w-full px-6',
                },
                displayName: 'Document',
                custom: {},
                parent: '',
                hidden: false,
                nodes: [],
                linkedNodes: {},
              },
            },
          },
        ],
      };
    }
    case FileFolderType.METHODS:
      return '//%attributes = {}\n';
    default:
      return '';
  }
}

export function getFilenameFromType(
  fileName: string,
  type: FileFolderType,
  qodly?: boolean,
) {
  switch (type) {
    case FileFolderType.MOBILEFORM:
      return `${fileName}.MForm`;
    case FileFolderType.WEBFORM:
      return `${fileName}.WebForm`;
    case FileFolderType.REPORT:
      return `${fileName}.QoDoc`;
    case FileFolderType.METHOD:
    case FileFolderType.CLASS:
      return `${fileName}.${qodly ? '4qs' : '4dm'}`;
    default:
      return fileName;
  }
}

export function castToFileFolder(type: string): FileFolderType {
  switch (type) {
    case FolderRootPath.WEBFORMS:
      return FileFolderType.WEBFORMS;
    case FolderRootPath.METHODS:
      return FileFolderType.METHODS;
    case FolderRootPath.CLASSES:
      return FileFolderType.CLASSES;
    case FolderRootPath.REPORTS:
    case 'Reports':
      return FileFolderType.REPORT;
    case 'Mobile':
      return FileFolderType.MOBILE;
    default:
      return type as FileFolderType;
  }
}

export function getFileType(root: FileFolderType): FileFolderType {
  switch (root) {
    case FileFolderType.WEBFORMS:
      return FileFolderType.WEBFORM;
    case FileFolderType.REPORT:
      return FileFolderType.REPORT;
    case FileFolderType.CLASSES:
      return FileFolderType.CLASS;
    case FileFolderType.METHODS:
      return FileFolderType.METHOD;
    case FileFolderType.FOLDER:
      return FileFolderType.FOLDER;
    case FileFolderType.MOBILE:
      return FileFolderType.MOBILEFORM;
    default:
      return root as FileFolderType;
  }
}

export const tryEditMethod = createAsyncThunk<
  ITab,
  {
    method: catalog.IMethod;
  },
  { state: AppState }
>('files/tryEditMethod', async ({ method }, { dispatch }) => {
  const filePathParts = method.filePath.split('/');
  const [fileName] = filePathParts[1].split('.');

  const { editor } = getEditors(method.filePath);

  const tab = await dispatch(
    openFileAtLine({
      file: {
        name: fileName,
        path: method.filePath,
        date: new Date().toISOString(),
        type: FileFolderType.CLASS,
        editor,
      },
      line: method.startingLine + 1,
    }),
  ).unwrap();

  return tab;
});

type TCreateModelFilesPayloadDataclass = {
  type: 'dataclass';
  className: string;
  collectionName: string;
  toCreate?: ClassFileTypes;
  expand?: boolean;
};

export type TCreateModelFilesPayload =
  | TCreateModelFilesPayloadDataclass
  | {
      type: 'model';
    };

export const createModelFiles = createAsyncThunk<
  void,
  TCreateModelFilesPayload,
  {
    state: AppState;
  }
>('explorer/createModelFiles', async (payload, { dispatch, getState }) => {
  const state = getState();
  const parentFolder = FolderRootPath.CLASSES;
  const qodly = state.settings.server.qodly;
  const parentNode = findNodeByPath(state.explorer, parentFolder);

  if (!parentNode) return;
  await dispatch(fetchFolderContent(parentFolder));

  const updatedState = getState();
  const files =
    updatedState.explorer.find((item) => item.name === parentFolder)
      ?.children || [];

  let filesToCreate: {
    content: string;
    name: string;
  }[] = [{ content: '', name: '' }];

  if (payload.type === 'dataclass' && !payload.toCreate) {
    const { className, collectionName } = payload;
    filesToCreate = [
      {
        ...getContent().dataclass({
          name: className,
          qodly,
        }),
      },
      {
        ...getContent().collection({
          name: collectionName,
          qodly,
        }),
      },
      {
        ...getContent().entity({
          name: className,
          qodly,
        }),
      },
    ];
  } else if (payload.type === 'dataclass' && payload.toCreate) {
    const { className, collectionName } = payload;

    filesToCreate = [
      {
        ...getContent()[payload.toCreate]({
          name: className || collectionName,
          qodly,
        }),
      },
    ];
  } else {
    filesToCreate = [
      {
        ...getContent().model({ name: 'DataStore', qodly }),
      },
    ];
  }

  filesToCreate = filesToCreate.filter(
    (item) =>
      !files.find(
        ({ name, ext, info: { exist = true } = {} }) =>
          name === item.name && ext === (qodly ? '4qs' : '4dm') && exist,
      ),
  );

  await Promise.all(
    filesToCreate.map(async ({ name, content }) => {
      await dispatch(
        createNewFile({
          type: FileFolderType.CLASS,
          path: parentFolder,
          fileName: name,
          parentNode,
          content,
          qodly: state.settings.server.qodly,
        }),
      );
    }),
  );

  const expandDataclass =
    payload.type === 'dataclass' && payload.expand ? payload.className : '';

  await dispatch(
    fetchFolderContent({ nodePath: parentFolder, expandDataclass }),
  );
});

export const fetchFolderContent = createAsyncThunk<
  ITreeItem[],
  | string
  | {
      nodePath: string;
      expandDataclass?: string;
    },
  {
    state: AppState;
  }
>(
  'explorer/fetchFolderContent',
  async (payload, { rejectWithValue, getState }) => {
    const { nodePath, expandDataclass = '' } =
      typeof payload === 'string' ? { nodePath: payload } : payload;
    const state = getState();
    try {
      const children = await getNodeInfo({
        verb: DesignerRequestVerb.GET_INFO,
        path: nodePath,
      });

      if (state.catalog.state !== 'loaded') return [];
      if (nodePath === 'Classes') {
        return createClassesFolderContent(
          state,
          children as INodeInfo[],
          expandDataclass,
        );
      }

      return (children as INodeInfo[]).map<ITreeItem>((child) => {
        const path = `${nodePath}/${child.name}${
          child.ext ? `.${child.ext}` : ''
        }`;
        const { editor, supportedEditors } = getEditors(path);

        return {
          name: child.name,
          size: child.size,
          type: child.type,
          date: `${child.date}`,
          flags: {},
          error: null,
          path,
          ext: child.ext,
          config: child.type === 'folder' ? {} : { editor, supportedEditors },
        };
      });
    } catch (e) {
      rejectWithValue('Failed loading file content please Reload');
      // handling the case of if the folder Classes doesn't exist backend side.
      if (nodePath === 'Classes') {
        return createClassesFolderContent(state, [], expandDataclass);
      }
      return [];
    }
  },
  {
    getPendingMeta: () => {},
  },
);

export const fetchClassesFolderContent = createAsyncThunk<
  ITreeItem[],
  void,
  {
    state: AppState;
  }
>('model/fetchClassesFolderContent', async (_, { getState }) => {
  const state = getState();
  const parentFolder = FolderRootPath.CLASSES;
  const parentNode = findNodeByPath(state.explorer, parentFolder);

  if (!parentNode) return;
  try {
    const children = await getNodeInfo({
      verb: DesignerRequestVerb.GET_INFO,
      path: parentFolder,
    });

    return createClassesFolderContent(state, children as INodeInfo[]);
  } catch (e) {
    return createClassesFolderContent(state, []);
  }
});

export const fetchFolderContentWithoutExpanding = createAsyncThunk<
  ITreeItem[],
  | string
  | {
      nodePath: string;
      collapseIfExpanded?: boolean;
    },
  {
    state: AppState;
  }
>(
  'explorer/fetchFolderContentWithoutExpanding',
  async (payload, { rejectWithValue, getState }) => {
    const { nodePath } =
      typeof payload === 'string' ? { nodePath: payload } : payload;
    const state = getState();
    try {
      const children = await getNodeInfo({
        verb: DesignerRequestVerb.GET_INFO,
        path: nodePath,
      });

      if (nodePath === 'Classes') {
        return createClassesFolderContent(state, children as INodeInfo[]);
      }

      return (children as INodeInfo[]).map<ITreeItem>((child) => {
        const path = `${nodePath}/${child.name}${
          child.ext ? `.${child.ext}` : ''
        }`;
        const { editor, supportedEditors } = getEditors(path);

        return {
          name: child.name,
          size: child.size,
          type: child.type,
          date: `${child.date}`,
          flags: {},
          error: null,
          path,
          ext: child.ext,
          config: child.type === 'folder' ? {} : { editor, supportedEditors },
        };
      });
    } catch (e) {
      return rejectWithValue('Failed loading file content please Reload');
    }
  },
  {
    getPendingMeta: () => {},
  },
);

function getFileName(nameWithExt: string): {
  name: string;
  ext: string;
} {
  const name = nameWithExt.split('.');

  name.pop();

  return {
    name: name.join('.'),
    ext: nameWithExt.split('.')[nameWithExt.split('.').length - 1],
  };
}

export const uploadFileAndGetResponse = createAsyncThunk<
  { status: number; data: unknown },
  File
>('explorer/uploadFileAndGetResponse', async (file) => {
  const QueryParams = file.type.includes('image/')
    ? '$rawPict=true'
    : '$binary=true';

  try {
    const res = await fetch(`/rest/$upload?${QueryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });
    return { status: res.status, data: await res.json() };
  } catch (ex) {
    return { status: 500, data: null };
  }
});

export const uploadFile = createAsyncThunk<
  { status: number; fileName: string },
  {
    file: File;
    path: string;
    parentNode: ITreeItem;
  },
  {
    state: AppState;
  }
>('explorer/uploadFile', async ({ file, path, parentNode }, { dispatch }) => {
  const { editor, supportedEditors } = getEditors(path);

  const { name, ext } = getFileName(file.name);

  dispatch(
    addNewFile({
      parent: parentNode,
      child: {
        name,
        path: `${path}/${file.name}?t=${Date.now()}`,
        type: file.type as FileFolderType,
        error: null,
        flags: {
          loading: true,
        },
        config: { editor, supportedEditors },
        ext,
      },
    }),
  );

  try {
    const res = await fetch(
      `/rest/$upload?$sharedFolder=true&$filename=${file.name}&$path=${path}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      },
    );

    if (res.status >= 300) {
      dispatch(
        removeFile({
          parent: parentNode,
          child: {
            name,
            path: `${path}/${file.name}`,
            type: file.type as FileFolderType,
            error: { message: 'error uploading file' },
            flags: {},
            config: { editor, supportedEditors },
            ext,
          },
        }),
      );

      if (res.status !== 413) {
        toastSubject.next({
          message: 'error uploading file',
          kind: 'danger',
        });
      }
    } else {
      await dispatch(fetchFolderContent(parentNode.path));
    }

    return { status: res.status, fileName: file.name };
  } catch (ex) {
    dispatch(
      removeFile({
        parent: parentNode,
        child: {
          name,
          path: `${path}/${file.name}`,
          type: file.type as FileFolderType,
          error: { message: 'error uploading file' },
          flags: {},
          config: { editor, supportedEditors },
          ext,
        },
      }),
    );

    toastSubject.next({
      message: 'error uploading file',
      kind: 'danger',
    });
    return { status: 500, fileName: file.name };
  }
});

export const createNewFile = createAsyncThunk<
  {
    qodly: boolean;
    file: ISetFileContentResponse;
    parentNode: ITreeItem;
  },
  ICreateNewFilePayload,
  {
    state: AppState;
    rejectValue: string;
  }
>(
  'explorer/createNewFile',
  async (
    { fileName, type, path, parentNode, content },
    { dispatch, rejectWithValue, getState },
  ) => {
    const {
      server: { qodly },
      project,
    } = getState().settings;
    try {
      const file = await setFileContent({
        name: getFilenameFromType(fileName, type, qodly),
        content: content || getDefaultContent(type, project),
        path,
        asText: [FileFolderType.CLASS, FileFolderType.METHOD].includes(type),
      });
      if (parentNode.error) {
        if (parentNode.flags.expanded) {
          await dispatch(fetchFolderContent(parentNode.path));
        } else {
          await dispatch(
            fetchFolderContentWithoutExpanding(parentNode.path),
          );
        }
      }
      return {
        qodly,
        file,
        parentNode,
      };
    } catch (e) {
      return rejectWithValue('failed to create folder');
    }
  },
);

/**
 * Returns the file path of the given file object based on its type.
 *
 * @param {ISaveFileResponse} file - The file object to get the path for.
 * @param {Partial<{ withExtension: boolean; qodly: boolean }>} [options] - Optional parameters object.
 * @param {boolean} [options.withExtension=true] - Whether to include the file extension in the path.
 * @param {boolean} [options.qodly=true] - Whether to use the qodly file extension instead of the standard one.
 * @return {string} The file path with or without extension, based on the options.
 */
export function getFilePath(
  file: ISaveFileResponse,
  options?: Partial<{
    withExtension: boolean;
    qodly: boolean;
  }>,
): string {
  const { qodly = true, withExtension = true } = options || {};
  const ext = getFileExtentionFromType(file.type, qodly);
  let path = '';

  switch (file.type) {
    case FileFolderType.WEBFORM:
      path = `${FolderRootPath.WEBFORMS}/${file.name}`;
      break;
    case FileFolderType.METHOD:
      path = `${FolderRootPath.METHODS}/${file.name}`;
      break;
    case FileFolderType.CLASS:
      path = `${FolderRootPath.CLASSES}/${file.name}`;
      break;
    default:
      return '';
  }

  return `${path}${withExtension ? `.${ext}` : ''}`;
}

export const createNewFileAndOpen = createAsyncThunk<
  void,
  ICreateNewFilePayload,
  {
    state: AppState;
  }
>(
  'explorer/createNewFileAndOpen',
  async (payload: ICreateNewFilePayload, { dispatch, getState }) => {
    const success = await dispatch(createNewFile(payload)).unwrap();
    const { qodly } = (getState() as AppState).settings.server;
    if (success) {
      const { file } = success;

      const path = getFilePath(file, { withExtension: false, qodly });

      const { editor } = getEditors(path);

      await dispatch(
        openFile({
          file: {
            name: payload.fileName,
            type: file.type,
            path,
            editor,
            date: new Date().toISOString(),
          },
        }),
      );
    }
  },
);

export const createNewNode = createAsyncThunk(
  'explorer/createNewNode',
  async (
    {
      fileName,
      path,
      parentNode,
    }: {
      fileName: string;
      type: FileFolderType;
      path: string;
      parentNode: ITreeItem;
    },
    { rejectWithValue },
  ) => {
    try {
      const file = await createFolder({
        verb: DesignerRequestVerb.CREATE_FOLDER,
        path: `${path}/${fileName}`,
        recursive: true,
      });

      return {
        file,
        parentNode,
      };
    } catch (e) {
      toastSubject.next({
        message: 'failed to create folder',
        kind: 'danger',
      });
      return rejectWithValue('failed to create folder');
    }
  },
);

const getFileTab = async ({
  file,
  flags = {},
  node = {} as ITreeItem,
}: {
  file: IFileInfo;
  flags?: ITabFlags;
  node?: ITreeItem;
}) => {
  try {
    const fileExt = file.path.split('.').at(-1);
    const response = (await getNodeInfo({
      verb: DesignerRequestVerb.GET_INFO,
      path: file.path,
      content: file.editor?.key !== 'editors:img',
      as: isJSON(fileExt) ? 'json' : 'text',
    })) as INodeInfo;

    const extra: ITabExtra = {
      editor: file.editor,
    };

    if (node?.info) {
      extra.kind = node.info.kind;
    }

    const type = getTypeFromExtension(file.path);

    if (type === FileFolderType.METHOD) {
      extra.attributes = parseAttributes(response.content);
      // response.content = removeAttributesString(response.content);
      response.content = addAttributesToContent(response.content, {});
    }

    const { content, date, ext } = response;

    if (flags.confirmed === undefined) {
      flags.confirmed = true;
    }

    const tab: ITab = {
      date,
      view: {
        panel: {
          isOpen: false,
          type: '',
          current: '',
        },
      },
      flags: {
        enabled: ['WebForm', 'MForm'].includes(ext),
        confirmed: flags.confirmed,
      },
      extra,
      name: file.name,
      path: file.path,
      type,
      initialContent: content,
      content,
      history: {
        stack: [],
        active: -1,
        locked: false,
      },
    };
    return tab;
  } catch (e) {
    const { data } = e as GetNodeInfoException;
    if (
      data.__ERROR.find((err) => err.errCode === ERROR_CODES.FILE_NOT_FOUND)
    ) {
      switch (file.path) {
        case ROLES_TAB_PATH: {
          const content = {
            permissions: {
              allowed: [],
            },
            privileges: [],
            roles: [],
          };
          return {
            date: new Date().toISOString(),
            flags,
            extra: {
              editor: {
                key: 'editors:roles',
              },
              kind: 'roles',
            },
            name: 'Roles & Privileges',
            path: file.path,
            type: FileFolderType.ROLES,
            initialContent: content,
            content,
          } as ITab;
        }
        case SETTINGS_TAB_PATH: {
          const content = {};
          return {
            date: new Date().toISOString(),
            flags,
            extra: {
              editor: {
                key: 'editors:settings',
              },
              kind: 'qodlyApp',
            },
            name: 'Settings',
            path: file.path,
            type: FileFolderType.SETTINGS,
            initialContent: content,
            content,
          } as ITab;
        }
      }
    }
    throw e;
  }
};

export const fetchHTTPHandlers = createAsyncThunk<
  void,
  void,
  {
    state: AppState;
    rejectValue: string;
  }
>('HTTPHandlers/fetch', async (_, { rejectWithValue }) => {
  const fileInfo = (
    await getFilesInfo({ path: HTTP_HANDLERS_PATH })
  ).files.find((file: { path: string }) => file.path === HTTP_HANDLERS_PATH);
  if (fileInfo?.deleted) {
    return rejectWithValue('not found');
  }
});

const getNodeByPath = (
  explorer: ITreeItem[],
  path: string,
): ITreeItem | undefined => {
  const searchChildren = (
    children: ITreeItem[],
    path: string,
  ): ITreeItem | undefined => {
    for (const child of children) {
      if (child.path === path) {
        return child;
      }

      if (child.children) {
        const found = searchChildren(child.children, path);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  };

  return searchChildren(explorer, path);
};

export const openFile = createAsyncThunk<
  ITab | null,
  IOpenFilePayload,
  {
    state: AppState;
  }
>(
  'explorer/openFile',
  async (
    { file, inactive, isNew, flags = {}, info },
    { getState, dispatch },
  ) => {

    const {
      root: { tabs },
      roles,
      explorer,
      debugger: { sessions },
      settings: {
        server: { qodly },
      },
    } = getState();
    const found = tabs.find(({ path }) => file.path === path);

    if (found) {
      const tab = {
        ...found,
        extra: {
          ...found?.extra,
          editor: file.editor,
        },
        flags: {
          ...found?.flags,
          ...flags,
        },
      };
      dispatch(selectTabByReference(tab));
      return tab;
    }

    if (file.path.startsWith('Debugger')) {
      const session = sessions.find(
        (session) => `Debugger/${session.threadId}` === file.path,
      );

      if (session) {
        const {
          source: { content },
          source,
        } = session.currentStackFrame;

        const tab: ITab = {
          name: file.name,
          type: FileFolderType.DEBUGGER,
          date: new Date().toISOString(),
          path: file.path,
          content,
          view: {
            panel: {
              isOpen: false,
              type: '',
              current: '',
            },
          },
          initialContent: content,
          flags: { confirmed: true },
          extra: {
            source,
            editor: file.editor,
          },
        };

        dispatch(selectTabByReference(tab));

        return tab;
      }
    }

    const node = getNodeByPath(explorer, file.path);
    let tab: ITab | null = null;

    try {
      if (file.path !== LOCALIZATION_PATH) {
        tab = await getFileTab({ file, flags, node });
      } else {
        tab = {
          date: new Date().toString(),
          view: {
            panel: {
              isOpen: false,
              type: '',
              current: '',
            },
          },
          flags: {
            enabled: false,
            confirmed: flags.confirmed,
          },
          extra: {
            editor: {
              key: 'editors:localization',
            },
          },
          name: file.name,
          path: file.path,
          type: FileFolderType.TEXT,
          initialContent: '',
          content: '',
          history: {
            stack: [],
            active: -1,
            locked: false,
          },
        };
      }
    } catch (e) {
      const { data } = e as GetNodeInfoException;
      if (
        data.__ERROR.find((err) =>
          [
            ERROR_CODES.JSON_MALFORMED,
            ERROR_CODES.JSON_MALFORMED_AT_LINE,
          ].includes(err.errCode),
        )
      ) {
        const { confirmed } = await dispatch(
          openModal({
            title: 'Corrupt file',
            type: ModalType.CONFIRM,
            color: ModalColor.WARNING,
            message: 'Open in code editor ?',
          }),
        ).unwrap();
        if (confirmed) {
          const responseAsText = (await getNodeInfo({
            verb: DesignerRequestVerb.GET_INFO,
            path: file.path,
            content: true,
            as: 'text',
          })) as INodeInfo;
          const { content, date, ext } = responseAsText;
          tab = {
            date,
            view: {
              panel: {
                isOpen: false,
                type: '',
                current: '',
              },
            },
            flags: {
              enabled: ['WebForm', 'MForm', 'QoDoc'].includes(ext),
              confirmed: true,
            },
            extra: {
              editor: EDITOR.Code,
            },
            name: file.name,
            path: file.path,
            type: getTypeFromExtension(file.path),
            initialContent: content,
            content,
            history: {
              stack: [],
              active: -1,
              locked: false,
            },
          };
        }
      }

      if (file.type === FileFolderType.CLASS && info) {
        // i need the content of files.
        const kind = info.kind as
          | 'dataclass'
          | 'userclass'
          | 'collection'
          | 'entity'
          | 'model'
          | 'app';
        const filename = `${file.name}.${qodly ? '4qs' : '4dm'}`;
        const content = getContent()[kind]({
          name: filename,
          qodly,
        }).content;

        const savedFile = await setFileContent({
          name: filename,
          content,
          path: 'Classes',
          asText: true,
        });

        tab = {
          date: savedFile.date,
          view: {
            panel: {
              isOpen: false,
              type: '',
              current: '',
            },
          },
          flags: {
            enabled: false,
            confirmed: true,
          },
          extra: {
            editor: EDITOR.Code,
          },
          name: file.name,
          path: file.path,
          type: getTypeFromExtension(file.path),
          initialContent: content,
          content,
          history: {
            stack: [],
            active: -1,
            locked: false,
          },
        };

        if (kind === 'app') {
          const dbMethodsInfo = await getFilesInfo(
            ...dbMethods.map((method) => ({
              path: `DatabaseMethods/${method.name}`,
            })),
          );
          dbMethods.forEach((method) => {
            const file = dbMethodsInfo.files.find(
              (file: { path: string }) =>
                file.path === `DatabaseMethods/${method.name}`,
            );
            if (file?.deleted) {
              setFileContent({
                name: method.name,
                content: method.content,
                path: 'DatabaseMethods',
                asText: true,
              });
            }
          });
        }
      }
      if (file.path === HTTP_HANDLERS_PATH) {
        const fileInfo = (
          await getFilesInfo({ path: HTTP_HANDLERS_PATH })
        ).files.find(
          (file: { path: string }) => file.path === HTTP_HANDLERS_PATH,
        );
        if (fileInfo?.deleted) {
          await setFileContent({
            name: HTTP_HANDLERS_PATH,
            content: '[]',
            path: './',
            asText: true,
          }).then(async () => {
            tab = await getFileTab({ file, flags, node });
          });
        }
      }
    }

    if (!tab) return tab;
    if (file.path === ROLES_TAB_PATH && !roles.flags.touched) {
      dispatch(
        setRolesContent({
          content: tab.content,
          date: tab.date,
          flags,
        }),
      );
    } else if (file.path === SETTINGS_TAB_PATH) {
      dispatch(
        setSettings({
          path: 'project',
          value: tab.content,
        }),
      );
    }
    const openTab = inactive ? openTabUnselected : selectTabByReference;

    dispatch(
      openTab({
        ...tab,
        isNew,
      }),
    );

    return tab;
  },
);

export const openFileAtLine = createAsyncThunk<
  ITab,
  {
    file: IFileInfo;
    line: number;
    offset?: number;
  },
  {
    state: AppState;
  }
>('explorer/openFileAtLine', async ({ file }, { getState, dispatch }) => {
  const {
    root: { tabs },
    explorer,
  } = getState();
  const found = tabs.find(({ path }) => file.path === path);

  if (found) {
    const tab = {
      ...found,
      extra: {
        ...found?.extra,
        editor: file.editor,
      },
    };
    dispatch(selectTabByReference(tab));
    return tab;
  }

  const node = getNodeByPath(explorer, file.path);

  const tab = await getFileTab({ file, node });
  dispatch(selectTabByReference(tab));

  return tab;
});

export const injectContentAtLine = createAsyncThunk(
  'explorer/injectContentAtLine',
  async (
    payload: { path: string; content: string; line: number },
    { getState, dispatch, rejectWithValue },
  ) => {
    const { explorer } = getState() as AppState;
    const filePath = payload.path.split(`/`);
    filePath.pop();

    const found = findNodeByPath(explorer, payload.path);

    if (!found)
      return rejectWithValue(
        `Error while trying to inject content in file ${payload.path}`,
      );

    const path = `${found.path}.${found.ext}`;
    const node = getNodeByPath(explorer, path);

    const tab = await getFileTab({
      file: {
        name: found.name,
        date: found.date || new Date().toISOString(),
        path,
        type: FileFolderType.CLASS,
        editor: found.config.editor,
      },
      node,
    });

    await dispatch(selectTabByReference(tab));

    return {
      ...payload,
      path: `${found.path}.${found.ext}`,
    };
  },
);

export interface IOpenFileAtSelectionPayload {
  file: IFileInfo;
  selection: ITextEditorSelection;
}

export const openFileAtSelection = createAsyncThunk<
  ITab,
  IOpenFileAtSelectionPayload,
  {
    state: AppState;
  }
>('explorer/openFileAtSelection', async ({ file }, { getState, dispatch }) => {
  const {
    root: { tabs },
    explorer,
  } = getState();
  const found = tabs.find(({ path }) => file.path === path);

  if (found) {
    const tab = {
      ...found,
      extra: {
        ...found?.extra,
        editor: file.editor,
      },
    };
    dispatch(selectTabByReference(tab));
    return tab;
  }

  const node = getNodeByPath(explorer, file.path);

  const tab = await getFileTab({ file, node });
  dispatch(selectTabByReference(tab));

  return tab;
});

export const deleteFile = createAsyncThunk<
  { deleted: boolean; node: ITreeItem },
  ITreeItem,
  { state: AppState }
>('explorer/deleteFile', async (file) => {
  const { name, type, path, flags } = file;

  if (flags.error) {
    return {
      deleted: true,
      node: file,
    };
  }

  const chunks = path.split('/');

  chunks.pop();

  const deleted = await dropFile({
    root: FolderRootType.ROOT,
    type: getFileType(type) as FileFolderTypeWithContent,
    verb: DesignerRequestVerb.DROP,
    name,
    path: chunks.join('/'),
  });

  return {
    deleted,
    node: file,
  };
});

export const deleteFolder = createAsyncThunk<
  { deleted: boolean; node: ITreeItem },
  ITreeItem,
  { state: AppState }
>('explorer/deleteFolder', async (file, { getState, dispatch }) => {
  const { path, flags } = file;

  if (flags.error) {
    return {
      deleted: true,
      node: file,
    };
  }

  const { deleted } = (await removeItem({
    force: true,
    path,
    verb: DesignerRequestVerb.REMOVE_ITEM,
  }).catch(console.error)) || { deleted: false };

  if (file.children) {
    const { tabs } = getState().root;
    file.children.forEach((child) => {
      const index = tabs.findIndex((tab) => tab.path === child.path);
      dispatch(closeTab(index));
    });
  }

  return {
    deleted,
    node: file,
  };
});

export const removeFileNode = createAsyncThunk<
  { deleted: boolean; node: ITreeItem },
  ITreeItem,
  { state: AppState }
>('explorer/removeFileNode', async (file, { getState, dispatch }) => {
  await removeItem({
    verb: DesignerRequestVerb.REMOVE_ITEM,
    path: file.path,
    force: true,
  }).catch(console.error);

  const {
    root: { tabs },
    catalog,
  } = getState();

  if (file.path === 'Classes/DataStore' || isSingleton(catalog, file)) {
    setTimeout(() => {
      dispatch(fetchCatalog());
    }, CATALOG_REFRESH_DELAY);
  }

  if (file.ext) {
    const index = tabs.findIndex((tab) => tab.path === file.path);

    if (index >= 0) {
      dispatch(closeTab(index));
    }
  }

  return {
    deleted: true,
    node: file,
  };
});

const isSingleton = (catalog: AppState['catalog'], file: ITreeItem) => {
  const {
    model: { singletons },
  } = catalog as { model: datasources.IEnhancedCatalog };
  return values(singletons).some((singleton) => singleton.name === file.name);
};

export const renameFile = createAsyncThunk<
  { old: ITreeItem; new: ITreeItem },
  { file: ITreeItem; newname: string },
  { state: AppState }
>('explorer/renameFile', async ({ file, newname }, { dispatch, getState }) => {
  if (file.type !== 'folder') {
    const chunks = file.path.split('/');

    chunks.pop();

    const path = `${chunks.join('/')}/${file.name}.${file.ext}`;
    const newPath = `${chunks.join('/')}/${newname}.${file.ext}`;
    await moveItem({
      verb: DesignerRequestVerb.MOVE_ITEM,
      path,
      newpath: newPath,
    });

    const { catalog } = getState();

    if (isSingleton(catalog, file)) {
      setTimeout(() => {
        dispatch(fetchCatalog());
      }, CATALOG_REFRESH_DELAY);
    }

    return {
      old: {
        ...file,
        path,
      },
      new: {
        ...file,
        name: newname,
        path: newPath,
      },
    };
  }

  const chunks = file.path.split('/');

  chunks.pop();

  await moveItem({
    verb: DesignerRequestVerb.MOVE_ITEM,
    path: `${chunks.join('/')}/${file.name}`,
    newpath: `${chunks.join('/')}/${newname}`,
  });

  return {
    old: file,
    new: {
      ...file,
      name: newname,
      path: `${chunks.join('/')}/${newname}`,
    },
  };
});

export const relocateNode = createAsyncThunk<
  void,
  { draggedNode: ITreeItem; newLocation: ITreeItem },
  { state: AppState }
>(
  'explorer/relocateNode',
  async ({ draggedNode, newLocation }, { rejectWithValue }) => {
    try {
      await moveItem({
        verb: DesignerRequestVerb.MOVE_ITEM,
        path: `${draggedNode.path}.${draggedNode.ext}`,
        newpath: `${newLocation.path}/${draggedNode.name}.${draggedNode.ext}`,
      });
    } catch (e) {
      // do nothing for now
      return rejectWithValue("can't move item");
    }
  },
);

export const duplicateNode = createAsyncThunk<any, any, { state: AppState }>(
  'explorer/duplicateNode',
  async (
    { node, duplicateName }: { node: ITreeItem; duplicateName: string },
    { rejectWithValue, fulfillWithValue, getState },
  ) => {
    const chunks = node.path.split('/');

    chunks.pop();

    try {
      const { tabs } = getState().root;

      const found = tabs.find(({ path }) => path === node.path);

      const asText = ['4dm', '4qm', '4qs'].includes(node.ext!);
      const { content } = found
        ? found
        : ((await getNodeInfo({
            verb: DesignerRequestVerb.GET_INFO,
            path: node.path,
            content: true,
            as: asText ? 'text' : 'json',
          })) as INodeInfo);

      const nodeCopy = await setFileContent({
        name: `${duplicateName}.${node.ext}`,
        content,
        path: chunks.join('/'),
        asText,
      });

      return fulfillWithValue({
        node: { ...nodeCopy, path: chunks.join('/') },
      });
    } catch (e) {
      return rejectWithValue('failed to duplicate file');
    }
  },
);

export const executeMethod = createAsyncThunk<
  void | {
    [refName: string]: datasources.IComponentAction;
  },
  {
    methodName: string;
    namespace?: string;
    params?: {
      name: string;
      namespace?: string;
      isHardCoded?: boolean;
      type?:
        | 'string'
        | 'number'
        | 'object'
        | 'date'
        | 'array'
        | 'bool'
        | undefined;
    }[];
    resultingDataSource?: {
      id?: string;
      namespace?: string;
    };
  },
  { state: AppState }
>(
  'explorer/executeMethod',
  async (
    { methodName, params = [], resultingDataSource = {}, namespace = '' },
    { rejectWithValue },
  ) => {
    try {
      const methodInst = window.Method4D.buildMethod(
        {
          methodName,
          parameters: params,
          resultingDataSource,
        },
        { namespace },
        null,
      );

      const response = await methodInst.callServerSide(
        { id: '', serverSideRef: '' },
        'onclick',
        {},
      );

      if (response?.__NOTIFICATION) {
        toastSubject.next({
          message: `${response.__NOTIFICATION?.message || ''}`,
          kind: `${(response.__NOTIFICATION?.type as ToastKind) || 'info'}`,
        });
      } else {
        toastSubject.next({
          message: 'Method executed Successfully',
          kind: 'message',
        });
      }

      return response;
    } catch (e: any) {
      // do nothing for now
      toastSubject.next({
        message:
          (e.errorStack.length > 1 && [
            {
              messages: e.errorStack.map(
                (err: { message: string }) => err.message,
              ),
            },
          ]) ||
          e.message ||
          `Error executing the method "**${methodName}**"`,
        kind: 'danger',
        markdown: true,
      });
      return rejectWithValue("can't execute method");
    }
  },
);
